import { useMemo } from 'react'
import { AuditTimeline } from '@/features/audit/audit-timeline'
import { useAuditLogQuery } from '@/features/audit/hooks/use-audit-log'
import { useUsersLookupQuery } from '@/features/users/hooks/use-users-query'
import { QueryErrorState } from '@/shared/components/query-error-state/query-error-state'
import { cn } from '@/shared/lib/utils'
import { Skeleton } from '@/shared/ui/skeleton'
import { useCreditDetailQuery } from '../hooks/use-credits-admin-v2-query'
import { creditAuditLabels } from '../operations/credit-audit-labels'

function CreditAuditSkeleton() {
  const skeletonRows = ['audit-a', 'audit-b', 'audit-c', 'audit-d', 'audit-e']
  return (
    <div className="flex w-full flex-col gap-5 px-4 py-4" aria-busy="true">
      {skeletonRows.map((row) => (
        <div key={row} className="flex gap-3">
          <Skeleton className="size-7 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * La bitácora del expediente, VIVIENDO EN EL SIDE PANEL.
 *
 * ── POR QUÉ NO ES UNA VISTA DE PÁGINA COMPLETA ───────────────────────────────
 *
 * Porque es material de CONSULTA, no de trabajo: se abre para contestar «¿quién movió esto?» y se
 * cierra. Ocupando la pantalla entera obligaba a abandonar el paso a paso —el lugar donde de verdad
 * se opera— para leer dos renglones, y volver costaba otro clic. En el panel se lee al lado del
 * expediente, que es la única forma en que un dato de auditoría sirve mientras se trabaja.
 *
 * ── EL ANCHO MANDA SOBRE EL LAYOUT ───────────────────────────────────────────
 *
 * Nada de `mx-auto max-w-3xl`: el panel mide entre 320 y 600 px (`SIDE_PANEL_WIDTH`) y una columna
 * centrada con tope de 48rem ahí adentro no centra nada — sólo agrega margen a un espacio que ya
 * falta. El encabezado tampoco repite «Bitácora»: ya lo dice el título del panel.
 */
export function CreditAuditLog({ creditId, className }: { creditId: string; className?: string }) {
  const query = useAuditLogQuery('credit', creditId)
  const { data: users = [] } = useUsersLookupQuery()

  // El diccionario de claves→nombres del expediente. Es la MISMA consulta que ya usa la página del
  // expediente (misma `queryKey`, `staleTime` de 30 s), así que abrir el panel al lado del paso a
  // paso no pide nada: sale de la caché. Y cuando la bitácora se abre sola desde el listado, el
  // costo es una lectura que llega mientras se leen los renglones — que hasta entonces se muestran
  // genéricos, nunca en claves.
  const detail = useCreditDetailQuery(creditId)
  const stages = detail.data?.stages
  const labels = useMemo(() => creditAuditLabels(stages), [stages])

  // La bitácora graba el actor con `name: null` a propósito (documentado en audit-log.schema.ts):
  // resolverlo acá es el contrato. Las entradas de créditos llevan el `usr_` público y las
  // legadas el `_id`, así que el mapa indexa por los dos espacios de id.
  //
  // ⚠️ El lookup v1 (`/users/lookup`) NO publica el `usr_` y además excluye a las cuentas admin, así
  // que este mapa NO alcanza para las entradas de crédito: el nombre lo sella la API al escribir
  // (`MongooseAuditRepository.sealActor`). Esto queda como red para lo legado.
  const usersById = new Map(
    users.flatMap((u) => [[u.id, u.fullname] as const, [u._id, u.fullname] as const])
  )
  const resolveActor = (id: string) => usersById.get(id) ?? 'Equipo interno'

  if (query.isLoading) return <CreditAuditSkeleton />
  if (query.isError || !query.data) {
    return (
      <QueryErrorState
        error={query.error}
        resource="la bitácora del crédito"
        className="min-h-60"
        onRetry={() => void query.refetch()}
      />
    )
  }

  return (
    <section
      className={cn(
        'w-full px-4 py-4 animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none',
        className
      )}
    >
      <p className="mb-5 text-xs text-muted-foreground">
        Acciones, cambios y decisiones registradas durante todo el expediente.
      </p>
      <AuditTimeline entries={query.data.items} resolveActor={resolveActor} labels={labels} />
    </section>
  )
}
